/**
 * Deployment wizard state management hook
 */

import { useState, useCallback } from 'react';
import type {
  DeploymentTarget,
  DeploymentStep,
  DeploymentState,
  DeploymentStepConfig,
  DeploymentLog,
  StepStatus,
  PrerequisiteCheck,
} from '@/lib/deployment/types';

const INITIAL_STEPS: DeploymentStepConfig[] = [
  {
    id: 'prerequisites',
    title: 'Check Prerequisites',
    description: 'Verify system requirements and dependencies',
    status: 'pending',
  },
  {
    id: 'environment',
    title: 'Configure Environment',
    description: 'Set up environment variables and credentials',
    status: 'pending',
  },
  {
    id: 'download',
    title: 'Download Blueprints',
    description: 'Download and prepare deployment files',
    status: 'pending',
  },
  {
    id: 'configure',
    title: 'Configure Services',
    description: 'Customize configuration for your environment',
    status: 'pending',
  },
  {
    id: 'deploy',
    title: 'Deploy Infrastructure',
    description: 'Execute deployment commands',
    status: 'pending',
  },
  {
    id: 'verify',
    title: 'Verify Deployment',
    description: 'Check service health and connectivity',
    status: 'pending',
  },
  {
    id: 'complete',
    title: 'Deployment Complete',
    description: 'Review deployment summary and next steps',
    status: 'pending',
  },
];

const DELL_INFERENCE_STACK_STEPS: DeploymentStepConfig[] = [
  {
    id: 'prerequisites',
    title: 'Prerequisites Check',
    description: 'Verify Terraform and network access',
    status: 'pending',
  },
  {
    id: 'configure',
    title: 'iDRAC Configuration',
    description: 'Provide server management credentials',
    status: 'pending',
  },
  {
    id: 'deploy',
    title: 'Infrastructure Provisioning',
    description: 'Mount ISO and execute Terraform',
    status: 'pending',
  },
  {
    id: 'verify',
    title: 'Post-Installation Setup',
    description: 'Run in-band configuration scripts',
    status: 'pending',
  },
  {
    id: 'complete',
    title: 'Deployment Complete',
    description: 'Review next steps and access details',
    status: 'pending',
  },
];

export function useDeploymentWizard(target: DeploymentTarget, blueprintId: string) {
  const [state, setState] = useState<DeploymentState>({
    target,
    blueprintId,
    currentStep: 'prerequisites',
    steps: blueprintId === 'dell-inference-stack' ? DELL_INFERENCE_STACK_STEPS : INITIAL_STEPS,
    logs: [],
    config: {},
  });

  const addLog = useCallback((log: Omit<DeploymentLog, 'timestamp'>) => {
    setState(prev => ({
      ...prev,
      logs: [...prev.logs, { ...log, timestamp: new Date() }],
    }));
  }, []);

  const updateStepStatus = useCallback((stepId: DeploymentStep, status: StepStatus) => {
    setState(prev => ({
      ...prev,
      steps: prev.steps.map(step =>
        step.id === stepId ? { ...step, status } : step
      ),
    }));
  }, []);

  const setCurrentStep = useCallback((step: DeploymentStep) => {
    setState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const updateConfig = useCallback((config: Record<string, any>) => {
    setState(prev => ({
      ...prev,
      config: { ...prev.config, ...config },
    }));
  }, []);

  const goToNextStep = useCallback(() => {
    const currentIndex = state.steps.findIndex(s => s.id === state.currentStep);
    if (currentIndex < state.steps.length - 1) {
      const nextStep = state.steps[currentIndex + 1];
      setCurrentStep(nextStep.id);
    }
  }, [state.steps, state.currentStep, setCurrentStep]);

  const goToPreviousStep = useCallback(() => {
    const currentIndex = state.steps.findIndex(s => s.id === state.currentStep);
    if (currentIndex > 0) {
      const prevStep = state.steps[currentIndex - 1];
      setCurrentStep(prevStep.id);
    }
  }, [state.steps, state.currentStep, setCurrentStep]);

  const startDeployment = useCallback(() => {
    setState(prev => ({ ...prev, startedAt: new Date() }));
    addLog({ level: 'info', message: 'Deployment wizard started' });
  }, [addLog]);

  const completeDeployment = useCallback(() => {
    setState(prev => ({ ...prev, completedAt: new Date() }));
    updateStepStatus('complete', 'completed');
    addLog({ level: 'success', message: 'Deployment completed successfully' });
  }, [addLog, updateStepStatus]);

  const resetWizard = useCallback(() => {
    setState({
      target,
      blueprintId,
      currentStep: 'prerequisites',
      steps: blueprintId === 'dell-inference-stack' ? DELL_INFERENCE_STACK_STEPS : INITIAL_STEPS,
      logs: [],
      config: {},
    });
  }, [target, blueprintId]);

  return {
    state,
    addLog,
    updateStepStatus,
    setCurrentStep,
    updateConfig,
    goToNextStep,
    goToPreviousStep,
    startDeployment,
    completeDeployment,
    resetWizard,
  };
}
